import type { ScheduleIntl } from './schedule-intl';

export interface ParsedWeekdayCron {
  minute: number;
  hour: number;
  days: number[];
}

const DAY_NAME_TO_NUMBER: Record<string, number> = {
  SUN: 0,
  MON: 1,
  TUE: 2,
  WED: 3,
  THU: 4,
  FRI: 5,
  SAT: 6,
};

/** Parses one day token (`"3"`, `"7"`, or a three-letter name) to 0=Sun..6=Sat, or null. */
function parseSingleDay(token: string): number | null {
  const trimmed = token.trim();
  if (trimmed === '') return null;

  const named = DAY_NAME_TO_NUMBER[trimmed.toUpperCase()];
  if (named !== undefined) return named;

  const num = Number(trimmed);
  if (!Number.isFinite(num) || !Number.isInteger(num)) return null;
  if (num === 7) return 0;
  if (num < 0 || num > 6) return null;
  return num;
}

/** Parses one comma-separated element (`"1"`, `"MON"`, `"1-5"`, `"mon-fri"`) to a list of days, or null. */
function parseDayElement(element: string): number[] | null {
  const rangeParts = element.split('-');
  if (rangeParts.length === 1) {
    const day = parseSingleDay(rangeParts[0]!);
    return day === null ? null : [day];
  }
  if (rangeParts.length !== 2) return null;

  const start = parseSingleDay(rangeParts[0]!);
  const end = parseSingleDay(rangeParts[1]!);
  if (start === null || end === null || end < start) return null;

  const days: number[] = [];
  for (let d = start; d <= end; d += 1) days.push(d);
  return days;
}

/**
 * Parses the day-of-week field of a cron expression: `*`, numeric lists
 * (`1,3`), numeric ranges (`1-5`), three-letter names case-insensitively
 * (`MON`, `mon-fri`), mixes of the above, and `7` as Sunday. Returns null
 * for anything else.
 */
function parseDayField(dow: string): number[] | null {
  if (dow === '*') return [0, 1, 2, 3, 4, 5, 6];

  const days = new Set<number>();
  for (const element of dow.split(',')) {
    const parsed = parseDayElement(element);
    if (parsed === null) return null;
    for (const d of parsed) days.add(d);
  }
  if (days.size === 0) return null;
  return [...days].sort((a, b) => a - b);
}

/**
 * Try to parse a cron expression as a simple weekday schedule
 * (`M H * * D1,D2,...`). Returns null for anything more complex.
 */
export function parseWeekdayCron(expr: string): ParsedWeekdayCron | null {
  const parts = expr.trim().split(/\s+/);
  if (parts.length !== 5) return null;

  const [minStr, hourStr, dom, month, dow] = parts;
  if (dom !== '*' || month !== '*') return null;
  if (!dow) return null;

  const minute = Number(minStr);
  const hour = Number(hourStr);
  if (!Number.isFinite(minute) || !Number.isFinite(hour)) return null;

  const days = parseDayField(dow);
  if (days === null) return null;

  return { minute, hour, days };
}

/**
 * Build a cron expression from local-time hour/minute and selected
 * days of the week (0=Sun..6=Sat). The resulting expression is
 * interpreted in the schedule's timezone by Temporal.
 */
export function buildWeekdayCron(minute: number, hour: number, days: number[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  return `${minute} ${hour} * * ${sorted.join(',')}`;
}

/** Whether the days are exactly Monday to Friday (0=Sun..6=Sat). */
export function isWeekdaysOnly(days: number[]): boolean {
  return days.length === 5 && [1, 2, 3, 4, 5].every((day) => days.includes(day));
}

/** 1 Jan 2023 was a Sunday, so `0=Sun..6=Sat` maps onto consecutive dates. */
const weekdayReferenceDate = (day: number): Date => new Date(Date.UTC(2023, 0, 1 + day));

function formatShortWeekdayName(intl: ScheduleIntl, day: number): string {
  return intl.formatDate(weekdayReferenceDate(day), {
    weekday: 'short',
    timeZone: 'UTC',
  });
}

/** Short labels: `[1, 3]` → `"Mon, Wed"` (localized via `Intl`). */
export function formatWeekdayNames(intl: ScheduleIntl, days: number[]): string {
  const sorted = [...days].sort((a, b) => a - b);
  return sorted.map((d) => formatShortWeekdayName(intl, d)).join(', ');
}

/** Select keys for the recurring-weekday message, indexed 0=Sun..6=Sat. */
const WEEKDAY_SELECT_KEYS = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
] as const;

/**
 * Localized display label for trigger badges.
 * Single day → recurring full name (`"Mondays"`);
 * multiple → localized short names (`"Mon, Wed"`).
 */
export function formatWeekdayLabel(intl: ScheduleIntl, days: number[]): string {
  if (isWeekdaysOnly(days)) {
    return intl.formatMessage({
      defaultMessage: 'Weekdays',
      id: '4YhYUNjIVr',
      description: 'Recurring schedule label for routines that run Monday to Friday',
    });
  }

  if (days.length === 1) {
    const dayKey = WEEKDAY_SELECT_KEYS[days[0]!];
    if (dayKey) {
      return intl.formatMessage(
        {
          defaultMessage:
            '{day, select, sunday {Sundays} monday {Mondays} tuesday {Tuesdays} wednesday {Wednesdays} thursday {Thursdays} friday {Fridays} saturday {Saturdays} other {Weekly}}',
          id: '9iF/ePfbML',
          description:
            'Recurring schedule label for the single weekday a routine runs on',
        },
        { day: dayKey }
      );
    }
  }

  const names = [...days]
    .sort((a, b) => a - b)
    .map((day) => formatShortWeekdayName(intl, day));
  return intl.formatList(names, { style: 'short', type: 'conjunction' });
}
